import { addDays, calendarDate, getActiveTimeZone } from './timezone'

/*
 * Everything about notifications that is a decision rather than a fetch.
 *
 * Kept free of React and of the dictionaries so the rules can be tested on
 * their own: which chip asks the server for which types, where yesterday ends,
 * and what one press of an e-mail switch has to send. The bell, the
 * notification centre and the profile card all read from here, so the three
 * cannot drift into three slightly different answers.
 */

// ---------------------------------------------------------------------------
// Types and importance
// ---------------------------------------------------------------------------

/** The server's stable keys, one per notification class. */
export const NOTIFICATION_TYPES = [
  'urgentSubmission',
  'submissionStatus',
  'improvementReviewed',
  'taskAssigned',
  'overdueDigest',
  'missingEvaluations',
  'dailySummary',
] as const

export type NotificationType = (typeof NOTIFICATION_TYPES)[number]

export type NotificationImportance = 'high' | 'normal' | 'low'

const IMPORTANCES: readonly string[] = ['high', 'normal', 'low']

/**
 * The importance a row gets when the server sent none.
 *
 * Rows written before the field existed carry no importance, and reading them
 * all as "normal" would leave last week's urgent report looking exactly like a
 * status change. The rule is the server's own: an urgent report and work given
 * to me are high, the scheduled reminders and the summary are low.
 */
export function importanceOf(type: string | null | undefined): NotificationImportance {
  switch (type) {
    case 'urgentSubmission':
    case 'taskAssigned':
      return 'high'
    case 'overdueDigest':
    case 'missingEvaluations':
    case 'dailySummary':
      return 'low'
    default:
      return 'normal'
  }
}

// ---------------------------------------------------------------------------
// One notification
// ---------------------------------------------------------------------------

/**
 * A notification as the API sends it.
 *
 * Every field the newer API adds is optional, and the older ones (`icon`,
 * `path`, `isRead`, `at`) are still read: the list holds rows written long
 * before `where` and `action` existed, and a server that has not been deployed
 * yet sends none of them at all.
 */
export interface RawNotification {
  id: string
  type?: string | null
  importance?: string | null
  title?: string | null
  body?: string | null
  context?: string | null
  where?: { workCenter?: string | null; plant?: string | null } | null
  action?: { label?: string | null; path?: string | null } | null
  icon?: string | null
  path?: string | null
  isRead?: boolean
  readAt?: string | null
  at?: string | null
  createdAt?: string | null
}

/** The one shape every screen renders. */
export interface AppNotification {
  id: string
  type: string | null
  importance: NotificationImportance
  title: string
  body: string
  context: string | null
  where: { workCenter: string | null; plant: string | null }
  /** `label` is null when only a path is known; the screen says "Otvori". */
  action: { label: string | null; path: string } | null
  icon: string | null
  isRead: boolean
  at: string | null
}

export function normalizeNotification(raw: RawNotification): AppNotification {
  const type = raw.type ?? null
  const actionPath = raw.action?.path ?? raw.path ?? null

  return {
    id: raw.id,
    type,
    importance:
      raw.importance && IMPORTANCES.includes(raw.importance)
        ? (raw.importance as NotificationImportance)
        : importanceOf(type),
    title: raw.title ?? '',
    body: raw.body ?? '',
    context: raw.context || null,
    where: {
      workCenter: raw.where?.workCenter || null,
      plant: raw.where?.plant || null,
    },
    action: actionPath ? { label: raw.action?.label || null, path: actionPath } : null,
    icon: raw.icon ?? null,
    // Either says it: `readAt` is the new field, `isRead` the old one.
    isRead: Boolean(raw.readAt) || raw.isRead === true,
    at: raw.createdAt ?? raw.at ?? null,
  }
}

/**
 * The small line under a notification: machine, plant, and whatever else the
 * server put in `context`.
 *
 * `context` is dropped when it only repeats the machine. A status change used
 * to carry the machine's name there, and showing "Presa 3 · Pogon 1 · Presa 3"
 * is the kind of noise that teaches people to stop reading the line.
 */
export function metaParts(notification: Pick<AppNotification, 'where' | 'context'>): string[] {
  const { workCenter, plant } = notification.where
  const parts = [workCenter, plant].filter((part): part is string => Boolean(part))

  if (notification.context && !parts.includes(notification.context)) {
    parts.push(notification.context)
  }

  return parts
}

/**
 * The loaded pages as one list, each notification once.
 *
 * Pages are offsets into a list that grows at the top. A notification that
 * arrives between loading page one and pressing "Učitaj još" pushes the last
 * row of page one onto page two, and it would show twice — once under its day
 * and again a screen further down. The first copy is the one kept.
 */
export function uniqueById<T extends { id: string }>(items: readonly T[]): T[] {
  const seen = new Set<string>()

  return items.filter((item) => {
    if (seen.has(item.id)) return false
    seen.add(item.id)
    return true
  })
}

// ---------------------------------------------------------------------------
// The unread badge
// ---------------------------------------------------------------------------

export interface UnreadCount {
  total: number
  high: number
}

/**
 * The badge's numbers, from either shape of the reply.
 *
 * `{data: {total, high}}` is the new one; `{count}` is what the API sent
 * before importance existed, and it is still what a server that has not been
 * updated sends. Reading both means the bell keeps counting through a deploy
 * in either order.
 */
export function parseUnreadCount(body: unknown): UnreadCount {
  const reply = (body ?? {}) as {
    count?: unknown
    data?: { total?: unknown; high?: unknown } | null
  }

  const number = (value: unknown) =>
    typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0

  const total = number(reply.data?.total ?? reply.count)

  // Never more urgent ones than there are unread ones, whatever a race between
  // two counts on the server produced.
  return { total, high: Math.min(number(reply.data?.high), total) }
}

// ---------------------------------------------------------------------------
// Filters on the notification centre
// ---------------------------------------------------------------------------

/**
 * The chips, as the person thinks of them rather than as the server stores
 * them.
 *
 * "Prijave" is three notification classes — an urgent report, a status
 * change, a reviewed improvement — and nobody looking for the report that just
 * changed cares which of the three it was. The id doubles as the URL value,
 * which is why it is Bosnian.
 */
export const NOTIFICATION_FILTERS = [
  { id: 'sve', types: [] },
  { id: 'prijave', types: ['urgentSubmission', 'submissionStatus', 'improvementReviewed'] },
  { id: 'zadaci', types: ['taskAssigned'] },
  { id: '5s', types: ['missingEvaluations'] },
  { id: 'podsjetnici', types: ['overdueDigest'] },
  { id: 'sazetak', types: ['dailySummary'] },
] as const satisfies readonly { id: string; types: readonly NotificationType[] }[]

export type NotificationFilterId = (typeof NOTIFICATION_FILTERS)[number]['id']

export function isNotificationFilterId(value: unknown): value is NotificationFilterId {
  return NOTIFICATION_FILTERS.some((filter) => filter.id === value)
}

/** The filter a URL value names, or "Sve" for anything else. */
export function filterFromParam(value: string | null | undefined): NotificationFilterId {
  return isNotificationFilterId(value) ? value : 'sve'
}

/**
 * The `type` the list endpoint is asked for: the keys, comma-separated.
 *
 * Undefined for "Sve" so the parameter is left off rather than sent empty.
 */
export function typeParam(filter: NotificationFilterId): string | undefined {
  const types: readonly string[] =
    NOTIFICATION_FILTERS.find((candidate) => candidate.id === filter)?.types ?? []

  return types.length > 0 ? types.join(',') : undefined
}

/**
 * The chips worth showing to this person.
 *
 * A chip for something the role never receives is a filter that can only ever
 * be empty — a worker has no use for "5S" when the 5S reminder goes to
 * managers. `available` is the preferences reply's answer to that; until it
 * arrives, or if it fails, every chip shows, since hiding one wrongly is worse
 * than showing one that stays empty. The chip already selected always stays,
 * so a link to it cannot land on a screen with no visible way back.
 */
export function visibleFilters(
  available: ReadonlySet<string> | null,
  selected: NotificationFilterId = 'sve',
): NotificationFilterId[] {
  return NOTIFICATION_FILTERS.filter(
    (filter) =>
      available === null ||
      filter.types.length === 0 ||
      filter.id === selected ||
      filter.types.some((type) => available.has(type)),
  ).map((filter) => filter.id)
}

// ---------------------------------------------------------------------------
// Day groups
// ---------------------------------------------------------------------------

export type DayGroupKind = 'today' | 'yesterday' | 'earlier' | 'date'

export interface DayGroup<T> {
  /** Stable across a language switch: the kind, or the date itself. */
  key: string
  kind: DayGroupKind
  /** `YYYY-MM-DD` for a `date` group, otherwise null. */
  date: string | null
  items: T[]
}

/**
 * Buckets the list by day, keeping the server's order.
 *
 * Fifty rows of "prije 8 h" tell you nothing about where yesterday ended.
 *
 * Where yesterday ended on the company's clock, compared as calendar dates:
 * the device's own midnight was an hour off on a phone set to the wrong zone,
 * and put the night shift's last hour under the wrong heading.
 *
 * `older` decides what happens before yesterday. The bell has room for one
 * more heading, "Ranije"; the notification centre is where you go to find
 * last Tuesday, so it gets a heading per date.
 *
 * Grouped by key rather than by translated heading. The key is the Map's
 * identity, so translating here would make "Danas" and "Today" two different
 * buckets — and a language switch with the panel open would split one day's
 * notifications across two headings.
 */
export function groupByDay<T extends { at: string | null }>(
  items: readonly T[],
  {
    older = 'earlier',
    todayDate,
    zone = getActiveTimeZone(),
  }: { older?: 'earlier' | 'date'; todayDate?: string; zone?: string } = {},
): DayGroup<T>[] {
  const todayKey = todayDate ?? calendarDate(new Date(), zone)
  const yesterdayKey = addDays(todayKey, -1)

  const groups = new Map<string, DayGroup<T>>()

  for (const item of items) {
    // `YYYY-MM-DD` compares correctly as a string, so `>=` still catches a
    // timestamp a skewed clock has put in the future.
    const at = item.at ? calendarDate(item.at, zone) : null

    let kind: DayGroupKind
    let date: string | null = null

    if (at === null) kind = 'earlier'
    else if (at >= todayKey) kind = 'today'
    else if (at >= yesterdayKey) kind = 'yesterday'
    else if (older === 'date') {
      kind = 'date'
      date = at
    } else kind = 'earlier'

    const key = date ?? kind
    const group = groups.get(key)

    if (group) group.items.push(item)
    else groups.set(key, { key, kind, date, items: [item] })
  }

  return [...groups.values()]
}

// ---------------------------------------------------------------------------
// Preferences
// ---------------------------------------------------------------------------

export interface NotificationPreference {
  key: string
  label: string
  description: string
  app: boolean
  mail: boolean
  /** False when this role never receives the type — such a row is not shown. */
  available: boolean
}

export interface NotificationPreferences {
  types: NotificationPreference[]
  hasEmail: boolean
}

export type PreferenceChannel = 'app' | 'mail'

export type PreferencePayload = Record<string, { app: boolean; mail: boolean }>

/** The rows the profile card shows, in the server's order. */
export function visiblePreferences(types: readonly NotificationPreference[]): NotificationPreference[] {
  return types.filter((type) => type.available)
}

/**
 * Whether one e-mail switch shows as on, and whether it can be pressed.
 *
 * Three things have to hold for mail to actually go out, and the switch shows
 * the result rather than any one of them: an address, the account's old
 * "send me e-mail as well" setting (still the server's master switch), and the
 * per-type choice. A switch that showed only the per-type choice would read
 * "on" for a person who receives nothing, which is the failure this card is
 * meant to end.
 */
export function mailSwitch(
  preference: Pick<NotificationPreference, 'mail'>,
  { hasEmail, emailEnabled }: { hasEmail: boolean; emailEnabled: boolean },
): { checked: boolean; disabled: boolean } {
  return {
    checked: hasEmail && emailEnabled && preference.mail,
    disabled: !hasEmail,
  }
}

/**
 * What one switch press sends.
 *
 * Normally just that type, with both channels, so the server never has to
 * guess the half it was not told.
 *
 * The one exception is turning e-mail on while the account's master e-mail
 * setting is off. Then every e-mail switch reads "off" (see `mailSwitch`) even
 * where the stored choice says yes, and turning the master on as a side effect
 * would silently start mail for all of those as well. So the press writes the
 * whole column as it looks — off everywhere but here — and then asks for the
 * master to be turned on.
 */
export function preferenceChange(
  types: readonly NotificationPreference[],
  key: string,
  channel: PreferenceChannel,
  value: boolean,
  { emailEnabled }: { emailEnabled: boolean },
): { types: PreferencePayload; enableEmail: boolean } {
  const target = types.find((type) => type.key === key)
  if (!target) return { types: {}, enableEmail: false }

  if (channel === 'mail' && value && !emailEnabled) {
    const payload: PreferencePayload = {}

    for (const type of visiblePreferences(types)) {
      payload[type.key] = { app: type.app, mail: type.key === key }
    }

    // The target is included even if the server marked it unavailable — it is
    // the row that was pressed.
    payload[key] = { app: target.app, mail: true }

    return { types: payload, enableEmail: true }
  }

  return {
    types: {
      [key]: {
        app: channel === 'app' ? value : target.app,
        mail: channel === 'mail' ? value : target.mail,
      },
    },
    enableEmail: false,
  }
}

/** The preferences as they will be once a change is saved — the optimistic copy. */
export function applyPreferenceChange(
  preferences: NotificationPreferences,
  payload: PreferencePayload,
): NotificationPreferences {
  return {
    ...preferences,
    types: preferences.types.map((type) =>
      payload[type.key] ? { ...type, ...payload[type.key] } : type,
    ),
  }
}
